<script setup lang="ts">
import { computed } from 'vue'
import type { Element, Action, TargetWidth } from '../types'
import { isVisibleAtWidth } from '../utils/targetWidth'
import { useCardContext } from '../composables/useCardContext'
import ACTextBlock from './ACTextBlock.vue'
import ACImage from './ACImage.vue'
import ACImageSet from './ACImageSet.vue'
import ACContainer from './ACContainer.vue'
import ACColumnSet from './ACColumnSet.vue'
import ACInputText from './ACInputText.vue'
import ACInputNumber from './ACInputNumber.vue'
import ACInputChoiceSet from './ACInputChoiceSet.vue'
import ACInputDate from './ACInputDate.vue'
import ACInputTime from './ACInputTime.vue'
import ACInputToggle from './ACInputToggle.vue'
import ACFactSet from './ACFactSet.vue'
import ACTable from './ACTable.vue'
import ACActionSet from './ACActionSet.vue'
import ACChartDoughnut from './ACChartDoughnut.vue'
import ACChartProgress from './ACChartProgress.vue'
import ACWidgetTabs from './ACWidgetTabs.vue'

const props = defineProps<{
  element: Element
  onAction?: (action: Action, inputs: Record<string, string>) => void
}>()

const { elements, cardWidth, visibility } = useCardContext()

const isVisible = computed(() => {
  // Check targetWidth visibility
  const targetWidth = (props.element as { targetWidth?: TargetWidth }).targetWidth
  if (!isVisibleAtWidth(targetWidth, cardWidth.value)) {
    return false
  }
  
  // Check element's own isVisible property (default true)
  const elementId = (props.element as { id?: string }).id
  const elementIsVisible = (props.element as { isVisible?: boolean }).isVisible
  
  // If element has an ID, check visibility state (toggled state takes precedence)
  if (elementId && elementId in visibility) {
    return visibility[elementId]
  }
  
  // Otherwise use element's isVisible property (default true)
  return elementIsVisible !== false
})

const hasSeparator = computed(() => {
  return (props.element as { separator?: boolean }).separator === true
})
</script>

<template>
  <template v-if="isVisible">
    <div v-if="hasSeparator" class="border-t border-border my-2" />
    <ACTextBlock v-if="props.element.type === 'TextBlock' || props.element.type === 'TextBlock.Markdown'" :element="props.element" />
    <ACImage
      v-else-if="props.element.type === 'Image'"
      :element="props.element"
      :on-action="props.onAction"
    />
    <ACImageSet
      v-else-if="props.element.type === 'ImageSet'"
      :element="props.element"
      :on-action="props.onAction"
    />
    <ACInputText v-else-if="props.element.type === 'Input.Text'" :element="props.element" />
    <ACInputNumber v-else-if="props.element.type === 'Input.Number'" :element="props.element" />
    <ACInputChoiceSet v-else-if="props.element.type === 'Input.ChoiceSet'" :element="props.element" />
    <ACInputDate v-else-if="props.element.type === 'Input.Date'" :element="props.element" />
    <ACInputTime v-else-if="props.element.type === 'Input.Time'" :element="props.element" />
    <ACInputToggle v-else-if="props.element.type === 'Input.Toggle'" :element="props.element" />
    <ACFactSet v-else-if="props.element.type === 'FactSet'" :element="props.element" />
    <ACTable
      v-else-if="props.element.type === 'Table'"
      :element="props.element"
      :on-action="props.onAction"
    />
    <ACActionSet
      v-else-if="props.element.type === 'ActionSet'"
      :element="props.element"
      :on-action="props.onAction"
      :elements="elements"
    />
    <ACContainer
      v-else-if="props.element.type === 'Container'"
      :element="props.element"
      :on-action="props.onAction"
    />
    <ACColumnSet
      v-else-if="props.element.type === 'ColumnSet'"
      :element="props.element"
      :on-action="props.onAction"
    />
    <ACChartProgress
      v-else-if="props.element.type === 'Chart.Progress'"
      :element="props.element"
    />
    <ACChartDoughnut
      v-else-if="props.element.type === 'Chart.Doughnut'"
      :element="props.element"
    />
    <ACWidgetTabs
      v-else-if="props.element.type === 'Widget.Tabs'"
      :element="props.element"
      :on-action="props.onAction"
    />
  </template>
</template>
